import { sessionStore } from '_clean-code/utils/storage';
import {
  DOCS_ORIGIN,
  IDocsAccessRequest
} from '_clean-code/api/docs/docsHandoff';

const STORAGE_KEY = 'pulsar-docs-return';
const MAX_AGE = 30 * 60 * 1000;

export function validGuidePath(value: string): boolean {
  return (
    typeof value === 'string' &&
    value.length <= 1800 &&
    /^\/(?:es\/)?guides(?:\/|#|$)/.test(value) &&
    !/[\\%?\s]/.test(value) &&
    !value.includes('//') &&
    !value.split(/[/#]/).some((part) => part === '.' || part === '..')
  );
}

export function readDocsRequest(): IDocsAccessRequest | null {
  const value = sessionStore.readJson<IDocsAccessRequest | null>(
    STORAGE_KEY,
    null
  );
  if (
    !value ||
    !/^[A-Za-z0-9_-]{43}$/.test(value.state) ||
    !validGuidePath(value.returnTo) ||
    !Number.isFinite(value.createdAt) ||
    Date.now() - value.createdAt > MAX_AGE ||
    value.createdAt > Date.now()
  )
    return null;
  return value;
}

export function parseDocsRequest(search: string): IDocsAccessRequest | null {
  const query = new URLSearchParams(search);
  const state = query.get('state');
  const returnTo = query.get('returnTo');
  if (
    !state ||
    !returnTo ||
    !/^[A-Za-z0-9_-]{43}$/.test(state) ||
    !validGuidePath(returnTo)
  )
    return null;
  return { state, returnTo, createdAt: Date.now() };
}

export function docsGateUrl(returnTo: string): string | null {
  if (!DOCS_ORIGIN) return null;
  const path = validGuidePath(returnTo) ? returnTo : '/guides';
  const gate = path.startsWith('/es/')
    ? '/es/guides-access/'
    : '/guides-access/';
  return DOCS_ORIGIN + gate + '?returnTo=' + encodeURIComponent(path);
}

export function saveDocsRequest(search: string): IDocsAccessRequest | null {
  const query = new URLSearchParams(search);
  if (!query.has('state') && !query.has('returnTo')) return readDocsRequest();
  const value = parseDocsRequest(search);
  if (!value) return null;
  sessionStore.writeJson(STORAGE_KEY, value);
  return value;
}

export function clearDocsRequest(): void {
  sessionStore.remove(STORAGE_KEY);
}

// The expired page restarts the sign-in at the article the reader asked for,
// read from the link itself or from the lapsed request, in their language.
export function docsRetryUrl(search: string, locale: string): string | null {
  if (!DOCS_ORIGIN) return null;
  const stored = sessionStore.readJson<Partial<IDocsAccessRequest> | null>(
    STORAGE_KEY,
    null
  );
  const returnTo = [
    new URLSearchParams(search).get('returnTo'),
    stored?.returnTo
  ].find((path): path is string => !!path && validGuidePath(path));
  return (
    DOCS_ORIGIN +
    '/auth/start?returnTo=' +
    encodeURIComponent(returnTo || (locale === 'es' ? '/es/guides' : '/guides'))
  );
}
